


































































import { defineComponent, computed, ref, Ref, watch, onMounted, onUnmounted } from '@vue/composition-api';
import { CircleSpinner, HexagonIcon } from '@nimiq/vue-components';
import UsdcTransactionListItem from '@/components/UsdcTransactionListItem.vue';
import { useI18n } from '@/lib/useI18n';
import { usePolygonAddressStore } from '../stores/PolygonAddress';
import { useUsdcTransactionsStore, Transaction, TransactionState } from '../stores/UsdcTransactions';
import { useUsdcContactsStore } from '../stores/UsdcContacts';
import { usePolygonNetworkStore } from '../stores/PolygonNetwork';
import { ENV_MAIN } from '../lib/Constants';
import { useConfig } from '../composables/useConfig';
import { useWindowSize } from '../composables/useWindowSize';
import { useUsdcTransactionInfo } from '../composables/useUsdcTransactionInfo';

function processTimestamp(timestamp: number) {
    const date: Date = new Date(timestamp);

    return {
        month: date.getMonth(),
        year: date.getFullYear(),
        date,
    };
}

function getLocaleMonthStringFromDate(
    date: Date,
    locale: string,
    options: {
        month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow',
        year?: 'numeric' | '2-digit',
    },
) {
    return new Intl.DateTimeFormat(locale, options).format(date);
}

// function getCloserElement(element: any, classToFind: string): HTMLElement {
//     let e = element as HTMLElement;
//
//     if (!e) throw new Error('element undefined');
//
//     const selector = `.${classToFind}`;
//
//     if (e.matches(selector)) return e;
//
//     const child = e.querySelector(`.${classToFind}`) as HTMLElement;
//     if (child) return child;
//
//     while (e && !e.matches(selector)) {
//         e = e.parentNode as HTMLElement;
//     }
//     return e;
// }

export default defineComponent({
    props: {
        searchString: {
            type: String,
            default: '',
        },
    },
    setup(props, context) {
        const { $t, locale } = useI18n();
        const { addressInfo } = usePolygonAddressStore();
        const { state: transactions$ } = useUsdcTransactionsStore();
        const { isFetchingUsdcTxHistory } = usePolygonNetworkStore();
        const { getLabel: getContactLabel } = useUsdcContactsStore();
        const { config } = useConfig();

        const activeAddress = computed(() => addressInfo.value?.address);

        // Amount of pixel to add to edges of the scrolling visible area to start rendering items further away
        const scrollerBuffer = 300;

        // Height of items in pixel
        const { isMobile } = useWindowSize();
        const itemSize = computed(() => isMobile.value ? 68 : 72); // mobile: 64px + 4px margin between items

        // Get all transactions for the active address
        const txsForActiveAddress = computed(() => Object.values(transactions$.transactions)
            .filter((tx) => tx.sender === activeAddress.value || tx.recipient === activeAddress.value));

        const txCount = computed(() => txsForActiveAddress.value.length);

        // Apply search filter
        const filteredTxs = computed(() => {
            if (!props.searchString) return txsForActiveAddress.value;

            const searchStrings = props.searchString.toUpperCase().split(' ').filter((value) => value !== '');

            return txsForActiveAddress.value.filter((tx) => {
                const transaction = ref<Readonly<Transaction>>(tx);
                const { peerLabel, data } = useUsdcTransactionInfo(transaction);

                const senderLabel = getContactLabel.value(tx.sender) || '';

                const recipientLabel = getContactLabel.value(tx.recipient) || '';

                const concatenatedTxStrings = `
                    ${tx.sender.replace(/\s/g, '')}
                    ${tx.recipient.replace(/\s/g, '')}
                    ${peerLabel.value ? (peerLabel.value as string).toUpperCase() : ''}
                    ${data.value ? (data.value as string).toUpperCase() : ''}
                    ${senderLabel.toUpperCase()}
                    ${recipientLabel.toUpperCase()}
                `;
                return searchStrings.every((searchString) => concatenatedTxStrings.includes(searchString));
            });
        });

        const transactions = computed(() => {
            // Display loading transactions
            if (!filteredTxs.value.length && isFetchingUsdcTxHistory.value) {
                // create just as many placeholders that the scroller doesn't start recycling them because the loading
                // animation breaks for recycled entries due to the animation delay being off.
                const listHeight = window.innerHeight - 220; // approximated to avoid enforced layouting by offsetHeight
                const placeholderCount = Math.floor((listHeight + scrollerBuffer) / itemSize.value);
                return [...new Array(placeholderCount)].map((e, i) => ({ transactionHash: i, loading: true }));
            }

            if (!filteredTxs.value.length) return [];

            // Sort transactions by descending timestamp
            const txs = filteredTxs.value.slice(0).sort((a, b) => {
                const aHeight = a.blockHeight || Number.MAX_SAFE_INTEGER;
                const bHeight = b.blockHeight || Number.MAX_SAFE_INTEGER;

                return bHeight - aHeight;
            });

            // Inject "This month" label
            const transactionsWithMonths: any[] = [];
            let isLatestMonth = true;

            const { month: currentMonth, year: currentYear } = processTimestamp(Date.now());
            let n = 0;
            let hasThisMonthLabel = false;

            if (txs[n].state === TransactionState.PENDING) {
                transactionsWithMonths.push({ transactionHash: $t('This month'), isLatestMonth });
                isLatestMonth = false;
                hasThisMonthLabel = true;
                while (txs[n] && txs[n].state === TransactionState.PENDING) {
                    transactionsWithMonths.push(txs[n]);
                    n++;
                }
            }

            // Skip expired & invalidated txs
            while (txs[n] && !txs[n].timestamp) {
                transactionsWithMonths.push(txs[n]);
                n++;
            }

            if (!txs[n]) return transactionsWithMonths; // Address has no more txs

            // Inject month + year labels
            let { month: txMonth, year: txYear } = processTimestamp(txs[n].timestamp! * 1000);
            let txDate: Date;

            if (!hasThisMonthLabel && txMonth === currentMonth && txYear === currentYear) {
                transactionsWithMonths.push({ transactionHash: $t('This month'), isLatestMonth });
                isLatestMonth = false;
            }

            let displayedMonthYear = `${currentMonth}.${currentYear}`;

            while (n < txs.length) {
                // Skip expired & invalidated txs
                if (!txs[n].timestamp) {
                    transactionsWithMonths.push(txs[n]);
                    n++;
                    continue;
                }

                ({ month: txMonth, year: txYear, date: txDate } = processTimestamp(txs[n].timestamp! * 1000));
                const txMonthYear = `${txMonth}.${txYear}`;

                if (txMonthYear !== displayedMonthYear) {
                    // Inject a month label
                    transactionsWithMonths.push({
                        transactionHash: getLocaleMonthStringFromDate(
                            txDate,
                            locale,
                            {
                                month: 'long',
                                year: txYear !== currentYear ? 'numeric' : undefined,
                            },
                        ),
                        isLatestMonth,
                    });
                    isLatestMonth = false;
                    displayedMonthYear = txMonthYear;
                }

                transactionsWithMonths.push(txs[n]);
                n++;
            }

            return transactionsWithMonths;
        });

        // listening for DOM changes for animations in the virtual scroll
        // TODO reconsider whether we actually want to have this animation. If so, fix it such that the animation
        // only runs on transaction hash change.
        const root: Ref<null | HTMLElement> = ref(null);
        // (() => {
        //     let txHashList = transactions.value.map((tx: Transaction) => tx.transactionHash + activeAddress.value);
        //     const config = { characterData: true, childList: true, subtree: true };
        //     const callback = async function mutationCallback(mutationsList: MutationRecord[]) {
        //         if (!transactions.value.length) return;
        //         const changedIndexes: string[] = [];
        //
        //         for (const mutation of mutationsList) {
        //             let element: null | HTMLElement = null;
        //
        //             if (mutation.target) {
        //                 if (
        //                     mutation.type === 'childList'
        //                     && !(mutation.target as HTMLElement).classList.contains('transaction-list')
        //                     && !(mutation.target as HTMLElement).classList.contains('resize-observer')
        //                 ) {
        //                     element = getCloserElement(mutation.target, 'list-element');
        //                 } else if (
        //                     mutation.type === 'characterData'
        //                     && mutation.target.parentNode
        //                 ) {
        //                     element = getCloserElement(mutation.target.parentNode, 'list-element');
        //                 }
        //             }
        //
        //             if (element && !changedIndexes.includes(element.dataset.id!)) {
        //                 changedIndexes.push(element.dataset.id!);
        //
        //                 const changedTxHash = element.dataset.hash as string;
        //
        //                 if (!txHashList.includes(changedTxHash + activeAddress.value)) { // added element
        //                     txHashList.push(changedTxHash + activeAddress.value);
        //                     element.classList.remove('fadein');
        //                     requestAnimationFrame(() => element!.classList.add('fadein'));
        //                 }
        //             }
        //         }
        //
        //         txHashList = transactions.value.map((tx: Transaction) => tx.transactionHash + activeAddress.value);
        //     };
        //
        //     const observer = new MutationObserver(callback);
        //
        //     onMounted(() => observer.observe(root.value!, config));
        //     onBeforeUnmount(() => observer.disconnect());
        // })();

        // Does not need to be reactive, as the environment doesn't change during runtime.
        const isMainnet = config.environment === ENV_MAIN;

        // Scroll to top when
        // - Active address changes
        const scroller = ref<{
            scrollToPosition(position: number, smooth?: boolean): void,
            $el: HTMLDivElement,
                } | null>(null);

        watch(activeAddress, () => {
            if (scroller.value) {
                scroller.value.scrollToPosition(0, false); // No smooth scrolling on address change
            }
        });

        function onScroll() {
            context.emit('scroll');
        }

        // @scroll / @scroll.native doesn't seem to work, so using standard event system
        onMounted(() => {
            if (!scroller.value) return;
            scroller.value.$el.addEventListener('scroll', onScroll);
        });

        onUnmounted(() => {
            if (!scroller.value) return;
            scroller.value.$el.removeEventListener('scroll', onScroll);
        });

        return {
            activeAddress,
            scrollerBuffer,
            itemSize,
            txCount,
            transactions,
            root,
            isFetchingUsdcTxHistory,
            isMainnet,
            scroller,
        };
    },
    components: {
        UsdcTransactionListItem,
        CircleSpinner,
        HexagonIcon,
    },
});
